import { FunctionComponent, useState } from 'react';
import { saveAs } from 'file-saver';
import { Button, Dialog } from '../../tailwindcomponents';
import { Print, Send } from '../icons';
import { HandoverReportDetails } from '../../types/handover';
import { HandoverReportConfig } from './handoverConfig';
import { buildHandoverPdfBlob, handoverPdfFileName } from './HandoverReportPdf';

type HandoverReportActionsProps = {
    config: HandoverReportConfig,
    report: HandoverReportDetails,
    /** Reports the outcome to the dialog, which owns the snackbar. */
    onResult: (message: string, isSuccess: boolean) => void,
    /** True while an issue row is being edited, which blocks emailing a half-typed report. */
    isEditingIssue?: boolean,
}

/**
 * Print and email actions for a handover report. Both are opt-in per handover
 * (`showPdfExport` / `showEmailReport` on the config), so only Line Planner shows them today.
 */
const HandoverReportActions: FunctionComponent<HandoverReportActionsProps> = ({
    config, report, onResult, isEditingIssue
}) => {
    const [pdfLoading, setPdfLoading] = useState<boolean>(false);
    const [emailLoading, setEmailLoading] = useState<boolean>(false);
    const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

    const busy = pdfLoading || emailLoading;
    // Emailing an unfinished handover to a shared mailbox is not wanted, so the action only
    // appears once the report has been closed. The backend enforces the same rule.
    const showEmail = config.showEmailReport === true && report.is_submitted === true;

    const buildPdf = () => buildHandoverPdfBlob({
        report,
        title: config.dialogTitle,
        employeeLabel: config.employeeLabel,
    });

    const onPrint = async () => {
        setPdfLoading(true);
        try {
            const blob = await buildPdf();
            saveAs(blob, handoverPdfFileName(report, config.dialogTitle));
        } catch {
            onResult("Could not generate the PDF", false);
        } finally {
            setPdfLoading(false);
        }
    }

    const onConfirmEmail = async (confirmed: boolean) => {
        setConfirmOpen(false);
        if (confirmed !== true) return;

        setEmailLoading(true);
        try {
            const res = await config.api.EmailReport(report.id);
            onResult(res.status === true ? res.message : (res.message ?? "Could not send the email"), res.status === true);
        } catch {
            onResult("Could not send the email", false);
        } finally {
            setEmailLoading(false);
        }
    }

    return (
        <div className="flex flex-wrap justify-end gap-2">
            {config.showPdfExport === true &&
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Print />}
                    disabled={busy}
                    onClick={() => onPrint()}
                >
                    {pdfLoading === false ? "Print" : "Please wait..."}
                </Button>
            }
            {showEmail &&
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Send />}
                    disabled={busy || isEditingIssue === true}
                    onClick={() => setConfirmOpen(true)}
                >
                    {emailLoading === false ? "Email" : "Sending..."}
                </Button>
            }
            <Dialog
                visible={confirmOpen}
                max_width={"md"}
                title={`Email this handover report to ${config.emailRecipientLabel ?? "the department"}?`}
                context={<div></div>}
                onClose={(status: any) => onConfirmEmail(status)}
                strict={false}
                show_options={true}
            />
        </div>
    );
};

export default HandoverReportActions;
